import React, { useEffect, useMemo, useState } from 'react';
import { X, Sparkles } from 'lucide-react';
import { useAppStore } from '@/store';

const ANNOUNCEMENT_KEY_BASE = 'mipt_announcement_ai_tips_v2';

const base64UrlDecode = (segment: string): any => {
  const base64 = segment.replace(/-/g, '+').replace(/_/g, '/');
  const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=');
  return JSON.parse(atob(padded));
};

const getTokenUserId = (): string | null => {
  const token = localStorage.getItem('access_token');
  if (!token) return null;
  try {
    const payload = base64UrlDecode(token.split('.')[1]);
    const id = payload?.user_id ?? payload?.sub ?? payload?.id;
    return id != null && id !== '' ? String(id) : null;
  } catch {
    return null;
  }
};

const getProfileKey = (profile: any): string | null => {
  if (profile?.id != null && profile.id !== '') return String(profile.id);
  if (profile?.user_details?.username) return String(profile.user_details.username);
  if (profile?.user_details?.email) return String(profile.user_details.email);
  return null;
};

const getStorageKey = (profile: any): string => {
  const key = getTokenUserId() ?? getProfileKey(profile);
  // Last resort: shared key so the announcement can still be dismissed forever.
  return key ? `${ANNOUNCEMENT_KEY_BASE}_${key}` : ANNOUNCEMENT_KEY_BASE;
};

/**
 * One-time modal introducing AI enhancement and Add Diagrams.
 * Shown exactly once per user account (remembered in localStorage keyed by the
 * JWT user_id / profile), closable via the × button or the "Got it" action.
 */
export const AdvancedFeatureAnnouncementModal: React.FC = () => {
  const { profile } = useAppStore();
  const storageKey = useMemo(() => getStorageKey(profile), [profile]);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    try {
      if (localStorage.getItem(storageKey) === 'seen') return;
      setOpen(true);
    } catch {
      setOpen(true);
    }
  }, [storageKey]);

  const dismiss = () => {
    try {
      // Re-resolve the key at dismissal time so it is persisted even when the
      // store profile was not hydrated when the modal opened.
      localStorage.setItem(getStorageKey(profile), 'seen');
    } catch {
      // Ignore storage failures; the modal closes for this session.
    }
    setOpen(false);
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[60] bg-black/50 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-label="AI enhancement tips"
    >
      <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-sm overflow-hidden max-h-[90vh] flex flex-col border border-gray-200">
        <button
          onClick={dismiss}
          aria-label="Close"
          className="absolute top-4 right-4 p-1.5 rounded-full text-gray-400 hover:text-gray-600 hover:bg-gray-100 z-10"
        >
          <X className="w-5 h-5" />
        </button>

        <div className="px-6 py-6 lg:px-7 lg:py-7 overflow-y-auto">
          <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-orange-700 bg-orange-50 border border-orange-100 rounded-full px-3 py-1 mb-3">
            <Sparkles className="w-3.5 h-3.5" />
            Did you know...
          </span>

          <h2 className="text-lg font-bold text-gray-900 mb-3">
            You can strengthen each weekly report with AI.
          </h2>

          <ul className="space-y-2.5 text-sm text-gray-700 leading-relaxed">
            <li>
              Open the AI Assistant from any report and ask it to improve the details — it
              points out what's missing, like hours, tools used, or supervisor notes.
            </li>
            <li>
              Add a diagram of your workplace sketches alongside the report.
            </li>
            <li>
              Everything works from your phone, no laptop needed.
            </li>
          </ul>

          <p className="mt-4 text-sm text-gray-600 leading-relaxed">
            A complete, detailed logbook earns better marks in your PT assessment.
          </p>

          <button
            onClick={dismiss}
            className="btn-primary w-full flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-semibold mt-5"
          >
            Got it
          </button>
        </div>
      </div>
    </div>
  );
};